/**
 * OPS-PREMIUM-UI-001 (Nov 2026) — compact animated Title Insurance banner shown
 * directly under the navigation bar on the Operations dashboard.
 * Decorative only: CSS/SVG animations, paused while the tab is hidden,
 * disabled under prefers-reduced-motion.
 */
import React, { useEffect, useState } from 'react';
import './ops-premium.css';

const PARTICLES = Array.from({ length: 16 });

// Two identical wave periods side by side → translateX(-50%) loops seamlessly.
const WAVE_A = 'M0 70 C 120 20, 240 20, 360 70 S 600 120, 720 70 S 960 20, 1080 70 S 1320 120, 1440 70 C 1560 20, 1680 20, 1800 70 S 2040 120, 2160 70 S 2400 20, 2520 70 S 2760 120, 2880 70';
const WAVE_B = 'M0 90 C 180 40, 300 130, 480 85 S 780 40, 960 90 S 1260 130, 1440 85 C 1620 40, 1740 130, 1920 85 S 2220 40, 2400 90 S 2700 130, 2880 85';
const WAVE_C = 'M0 50 C 200 100, 340 10, 540 55 S 880 100, 1080 50 S 1300 10, 1440 55 C 1640 100, 1780 10, 1980 55 S 2320 100, 2520 50 S 2740 10, 2880 55';

export const OpsHeroBanner = () => {
  const [paused, setPaused] = useState(typeof document !== 'undefined' && document.hidden);

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return (
    <section
      className="ops-hero"
      data-anim={paused ? 'paused' : 'running'}
      aria-label="Title Insurance Operations"
      data-testid="ops-hero-banner"
    >
      <div className="ops-hero__glow ops-hero__glow--l" aria-hidden="true" />
      <div className="ops-hero__glow ops-hero__glow--r" aria-hidden="true" />

      <svg className="ops-hero__waves" viewBox="0 0 1440 140" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="opsWaveBlue" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#38bdf8" stopOpacity="0" />
            <stop offset="0.25" stopColor="#60a5fa" stopOpacity="0.95" />
            <stop offset="0.6" stopColor="#a78bfa" stopOpacity="0.95" />
            <stop offset="1" stopColor="#38bdf8" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="opsWaveViolet" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#c084fc" stopOpacity="0" />
            <stop offset="0.4" stopColor="#e879f9" stopOpacity="0.85" />
            <stop offset="0.75" stopColor="#818cf8" stopOpacity="0.9" />
            <stop offset="1" stopColor="#c084fc" stopOpacity="0" />
          </linearGradient>
          <filter id="opsGlow" x="-10%" y="-50%" width="120%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        <g className="ops-wave ops-wave--1">
          <path d={WAVE_A} stroke="url(#opsWaveBlue)" strokeWidth="26" fill="none" filter="url(#opsGlow)" opacity="0.35" />
          <path d={WAVE_A} stroke="url(#opsWaveBlue)" strokeWidth="9" fill="none" filter="url(#opsGlow)" opacity="0.8" />
          <path d={WAVE_A} stroke="#e0f2fe" strokeWidth="1.6" fill="none" opacity="0.9" />
        </g>
        <g className="ops-wave ops-wave--2">
          <path d={WAVE_B} stroke="url(#opsWaveViolet)" strokeWidth="22" fill="none" filter="url(#opsGlow)" opacity="0.32" />
          <path d={WAVE_B} stroke="url(#opsWaveViolet)" strokeWidth="7" fill="none" filter="url(#opsGlow)" opacity="0.75" />
          <path d={WAVE_B} stroke="#fae8ff" strokeWidth="1.3" fill="none" opacity="0.85" />
        </g>
        <g className="ops-wave ops-wave--3">
          <path d={WAVE_C} stroke="url(#opsWaveBlue)" strokeWidth="5" fill="none" filter="url(#opsGlow)" opacity="0.5" />
          <path d={WAVE_C} stroke="#bae6fd" strokeWidth="1" fill="none" opacity="0.75" />
        </g>
      </svg>

      <div className="ops-hero__particles" aria-hidden="true">
        {PARTICLES.map((_, i) => (
          <span key={i} className="ops-particle" style={{ '--i': i }} />
        ))}
      </div>

      <div className="ops-hero__inner">
        <div className="ops-hero__copy">
          <h2 className="ops-hero__title" data-testid="ops-hero-title">TITLE INSURANCE OPERATIONS</h2>
          <p className="ops-hero__subtitle" data-testid="ops-hero-subtitle">Orders • Taxes • MLS • Title Search</p>
        </div>
        <div className="ops-hero__art" aria-hidden="true">
          <span className="ops-orb ops-orb--1" />
          <span className="ops-orb ops-orb--2" />
          <span className="ops-hero__line" />
        </div>
      </div>
    </section>
  );
};

export default OpsHeroBanner;
